<template>
  <div class="chatbot-page" :key="componentKey">
    <div class="header__wrapper">
      <!-- Back button - Title - Last time saved -->
      <header class="header__details">
        <a-button type="link" class="back-button" @click="handleBack">
          <ArrowLeftOutlined />
          Volver
        </a-button>

        <div class="title">
          <h2 class="name">{{ botDetails?.name || "Cargando..." }}</h2>

          <!--Estado de chatbot-->
          <!-- <a-tag v-if="botDetails.status" :color="StatusProps[botDetails.status].color">
            {{ StatusProps[botDetails.status].text }}
          </a-tag> -->
        </div>

        <div v-if="botNodes.length != 0" class="save-time">
          Guardado por ultima vez:
          {{ dateTimeToSlashFormat(botDetails?.updated_at, true) || "..." }}
        </div>
      </header>
    </div>

    <!-- Tabs - Status switch -->
    <a-tabs id="ivr-tabs" v-model:activeKey="current" centered destroyInactiveTabPane>
      <template #rightExtra>
        <div class="extra-action">
          <a-tag v-if="isTransferWithoutQueue" color="orange">
            <a-tooltip
              title="Este IVR se encontraba utilizando una cola que ha sido eliminada. Podrás volver a activarlo cuando selecciones una nueva cola de transferencia."
              placement="bottomLeft"
              arrowPointAtCenter
            >
              <WarningOutlined />
            </a-tooltip>
            Cola Eliminada
          </a-tag>

          <DisableSwitch
            inTemplate
            v-if="botDetails"
            :bot="botDetails"
            :disabled="!isBotActive && isTransferWithoutQueue"
            @update="fetchBot"
          />
        </div>
      </template>

      <a-tab-pane :key="TemplateTabsKey.BuildBot" tab="Construir IVR">
        <TreeLayout :botDetails="botDetails" :disabled="isBotActive" :deleted-queues="isTransferWithoutQueue" @refresh-bot="fetchBot" />
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.GeneralData" tab="Datos generales">
        <div class="container-tab">
          <h3>Datos generales</h3>
          <DetailsStep
            ref="initialStep"
            from-detail-template
            :botDetails="botDetails"
            :key="formKey"
            :disabled="isBotActive"
            @toggle-activate-button="handleToggleButton"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane
        v-if="botDetails?.type === BotType.Automatic"
        :key="TemplateTabsKey.Schedule"
        tab="Horario de atención"
      >
        <div class="container-tab">
          <h3>Horario de atención</h3>
          <SchedulesStep
            ref="timeStep"
            from-detail-template
            :botDetails="botDetails"
            :key="formKey"
            :disabled="isBotActive"
            @toggle-activate-button="handleToggleButton"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane
        v-if="botDetails?.type === BotType.Automatic"
        :key="TemplateTabsKey.Identifiers"
        tab="Identificadores vinculados"
      >
        <div class="container-tab">
          <h3>Identificadores de voz vinculados</h3>
          <IdentifiersStep
            ref="channelStep"
            from-detail-template
            :botDetails="botDetails"
            :key="formKey"
            :disabled="isBotActive"
            @toggle-activate-button="handleToggleButton"
          />
        </div>
      </a-tab-pane>
    </a-tabs>

    <footer v-if="current !== TemplateTabsKey.BuildBot">
      <a-button @click="handleCancel" :disabled="isBotActive"> Cancelar </a-button>
      <a-button
        type="primary"
        @click="handleUpdateBot"
        :disabled="isUpdateButtonDisabled || isBotActive"
        :loading="loading"
      >
        Actualizar {{ TabSectionName[current] }}
      </a-button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, provide, computed, watch, watchEffect } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { ArrowLeftOutlined, WarningOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

import { BotStatus, BotType, TemplateTabsKey } from "@/app/ivr/bot/utils/enums";
import { botServices } from "@/app/ivr/bot/services/BotServices";
import { nodeServices } from "@/app/ivr/bot/services/NodeServices";
import { dateTimeToSlashFormat } from "@/app/shared/utils/dates";
import { formatUpdateFormState } from "@/app/chatbots/utils/helpers";
import { Bot, Node } from "@/@types/ivr";
import { ResponseError } from "@/app/shared/utils/httpManager";
import {
  FetchBotDetailsKey,
  FetchNodesKey,
  BotDetailsKey,
  BotNodesKey,
} from "@/@types/ivr/symbols";

import TreeLayout from "@/app/ivr/bot/components/tree/TreeLayout.vue";
import DisableSwitch from "@/app/ivr/bot/components/buttons/DisableSwitch.vue";
import DetailsStep from "@/app/ivr/bot/components/steps/DetailsStep.vue";
import SchedulesStep from "@/app/ivr/bot/components/steps/SchedulesStep.vue";
import IdentifiersStep from "@/app/ivr/bot/components/steps/IdentifiersStep.vue";

const TabSectionName = {
  [TemplateTabsKey.BuildBot]: "bot",
  [TemplateTabsKey.GeneralData]: "datos generales",
  [TemplateTabsKey.Schedule]: "horario",
  [TemplateTabsKey.Identifiers]: "identificadores",
};

const store = useStore();
const route = useRoute();
const router = useRouter();

const current = ref<TemplateTabsKey>(TemplateTabsKey.BuildBot);

const botDetails = ref<Bot>();
const botNodes = ref<Node[]>([]);
const queuesOptions = ref([]);

const isBotActive = computed(() => botDetails.value?.status === BotStatus.Active);
const isTransferWithoutQueue = computed(() => botDetails.value?.deleted_queues);

const componentKey = ref<number>(0);

const fetchBot = async () => {
  botDetails.value = await botServices.getDetails(Number(route.params.id));
  componentKey.value += 1;

  // queuesOptions.value = botDetails.value?.channels_customers?.map(({ queues }) => ({
  //   value: queues?.[0]?.id,
  //   label: queues?.[0]?.name,
  // }));
};

const fetchNodes = async () => {
  botNodes.value = await nodeServices.getBotNodes(Number(route.params.id));
};

const loadingBot = ref(false);

const initialFetch = async () => {
  loadingBot.value = true;

  await Promise.all([fetchBot(), fetchNodes()]);

  loadingBot.value = false;
}

onMounted(async () => {
  // await fetchBot();
  // await nodeServices.getBotNodes();
  // store.dispatch("fetchNodes", route.params.id);
  if (route.query.key == 'connector') {
    router.push({
      name: "edit-connector",
      params: {connectorId: route.params.id},
    });
    return;
  }
  
  await initialFetch();
});

provide(FetchBotDetailsKey, fetchBot);
provide(FetchNodesKey, fetchNodes);
provide("isBotActive", isBotActive);
provide(BotDetailsKey, botDetails);
provide(BotNodesKey, botNodes);
store.commit("SET_QUEUES_SELECT_OPTIONS", queuesOptions);

const handleBack = () => {
  const isAutomatic = route.path.includes("automatic");

  if (isAutomatic) router.push("/flows/ivr-automatic");
  else router.push("/flows/ivr-survey");
};

// Update Logic
const formKey = ref(1);
const formState = ref({});
const isUpdateButtonDisabled = ref(true);

const initialStep = ref<InstanceType<typeof DetailsStep>>();
const timeStep = ref<InstanceType<typeof SchedulesStep>>();
const channelStep = ref<InstanceType<typeof IdentifiersStep>>();

const RefByCurrentTab = {
  [TemplateTabsKey.BuildBot]: undefined,
  [TemplateTabsKey.GeneralData]: initialStep,
  [TemplateTabsKey.Schedule]: timeStep,
  [TemplateTabsKey.Identifiers]: channelStep,
};

const handleToggleButton = (isDisabled: boolean) => {
  isUpdateButtonDisabled.value = isDisabled;
};

const handleDisable = () => {
  isUpdateButtonDisabled.value = true;
};

const loading = ref(false);

const handleUpdateBot = async () => {
  loading.value = true;

  try {
    formState.value = {
      ...RefByCurrentTab[current.value]?.value?.formState,
    };

    if (formState.value && "submitForm" in formState.value) {
      const result = await formState.value.submitForm!();

      let isValid: boolean;

      if (result) {
        isValid = result.length ? result.every((i: boolean) => i) : true;

        if (!isValid) return;
      } else {
        isValid = true;
      }
    }

    const body = formatUpdateFormState(formState.value);

    const fileAudio = {
      out_of_schedule_file_audio: formState.value?.out_of_schedule_file_audio,
    };

    const { data } = await botServices.update(Number(route.params.id), body);

    await botServices.uploadOutOfServiceAudio(data.id, fileAudio);

    await fetchBot();

    message.success(`Se ha actualizado el IVR correctamente `);
    handleDisable();
  } catch (error) {
    const errorMessage =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.warning(errorMessage);
    console.log(error);
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  formKey.value += 1;
  handleDisable();
};

watch(current, () => {
  handleDisable();
});
</script>

<style lang="sass" scoped>
.chatbot-page
  width: 100%
  height: 100%

.header
  &__wrapper
    width: 100%
    padding: 6px 12px 0
    background-color: $white

  &__details
    display: flex
    justify-content: center
    align-items: center
    position: relative
    width: 100%

    .back-button
      padding: 0
      position: absolute
      left: 0

    .title
      display: flex
      align-items: center
      gap: 8px

      .name
        font-size: 16px
        font-weight: 600
        color: $gray-9
        margin: 0

    .save-time
      font-style: italic
      font-weight: 300
      font-size: 12px
      position: absolute
      right: 0

.extra-action
  display: flex
  align-items: center
  min-width: 200px
  justify-content: flex-end

:deep(.ant-tabs-nav)
  background-color: $white
  margin-bottom: 12px !important

:deep(.ant-tabs)
  height: calc(100% - 30px)

:deep(.ant-tabs-content)
  height: 100%

:deep(.ant-tabs-extra-content)
  position: absolute
  right: 12px

:deep(.ant-tabs-tabpane)
  height: 100%

:deep(.ant-tabs-tabpane:not(:first-child))
  background-color: $white

.row
    display: grid
    grid-template-columns: 0.2fr 1fr
    gap: 0px
.container
  &-tree
    padding: 12px
    height: 100%

  &-config
    height: 100%

  &-tab
    margin: 0 auto
    max-width: 50%
    height: calc(100% - 80px)

    h3
      color: $gray-9
      font-size: 16px
      font-weight: 400
      border-bottom: 1px solid $gray-5
      padding: 20px 0 10px
      margin-bottom: 10px

footer
  padding: 14px
  display: flex
  justify-content: flex-end
  gap: 12px
  max-width: 50%
  border-top: 1px solid $gray-5
  margin: -62px auto 0
  position: relative
</style>
